"use client";
import ProductDummyImg from "@/assets/images/category-card-product-img1.png";
import DynamicImage from "@/components/DynamicImages/DynamicImage";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { capitalizeFirstLetter, cn } from "@/lib/utils";
import { categoriesApiSlice } from "@/redux/apiSlices/categoriesApi";
import { filtersApiSlice } from "@/redux/apiSlices/filtersApi";
import { AddBreadcrum, AddNewBreadcrum } from "@/redux/slices/breadcrumSlice";
import { useQueryParams } from "@/utils/useQueryParams";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

const TopSlider = ({
  slug,
  filterBy,
  filterDetails = {},
  isBrandPage,
  isLuxuryBrand,
  isShop,
}) => {
  const pathname = usePathname();
  const dispatch = useDispatch();
  const {
    categoryIds,
    subCategoryIds,
    subCategory2Ids,
    fieldIds: existingFieldIds,
    fieldValueIds: existingFieldValuesIds,
    keyword,
    rebateIds,
    brandIds,
    onDisplay,
    isAvailabilty,
    fieldIds,
    fieldValueIds,
    storeId,
    zipCode,
    deliveryDate,
    onSale,
    clearance,
    rebateAvailable,
  } = useQueryParams();

  const [categorySlug = "", subcategorySlug] = slug;

  const { categoryList = [] } = filterDetails;

  const combineSlug = subcategorySlug
    ? `${categorySlug}/${subcategorySlug}`
    : categorySlug;
  const customFilterSlugs = [
    "top-deals",
    "in-stock",
    "next-day-delivery",
    "outlet",
    "open-box-appliances",
    "mail-in-rebate-offers",
  ];

  const isCustomFilter = customFilterSlugs.includes(categorySlug);
  const isSmartAppliances = categorySlug.includes("smart");
  const isSmartDishwashers = subcategorySlug?.includes("smart-dishwashers");

  const categoriesData =
    useSelector(
      (state) =>
        categoriesApiSlice.endpoints.getCategories.select()(state)?.data?.data
    ) || [];

  const breadcrumbItems = useSelector((state) => state.breadcrumbs);

  const addBreadcrum = (isSubCategory2, category, catList, breadcrumObj) => {
    if (isSubCategory2 && !isBrandPage && !isLuxuryBrand) {
      const isExist = breadcrumbItems?.some((cat) =>
        catList?.some((cat2) => cat.name === cat2.subCategory2Name)
      );
      if (isExist) {
        const newArr = breadcrumbItems.slice(0, -1).concat(breadcrumObj);
        dispatch(AddNewBreadcrum(newArr));
      } else {
        dispatch(AddBreadcrum(breadcrumObj));
      }
    } else {
      const lastElement = breadcrumbItems[breadcrumbItems.length - 1];
      const isExistInLast = lastElement.url === breadcrumObj.url;
      if (isExistInLast) return;
      dispatch(AddBreadcrum(breadcrumObj));
    }
  };

  const { data } =
    useSelector((state) =>
      filtersApiSlice.endpoints.getFiltersList.select({
        categorySlug: filterBy,
        rebateId: rebateIds,
        brandIds,
        isAvailabilty,
        zipCode,
        deliveryDate,
        onDisplay,
        storeId,
        onSale,
        clearance,
        rebateAvailable,
        fieldIds,
        fieldValueIds,
      })(state)
    ) || [];

  const { categoriesData: customFilterData = [] } = data || {};

  const subCategoriesData = useMemo(() => {
    if (!categoriesData && !isCustomFilter) return null;
    if (isCustomFilter || isBrandPage || isLuxuryBrand || isShop) {
      return {
        category: categoryList || [],
      };
    }

    const findCategoryBySlug = (data, slug) => {
      for (const cat of data) {
        // Check at the current category level
        if (cat.slug === slug) {
          return cat;
        }

        // Check in subcategories and subcategories2
        if (cat.subcategories?.length) {
          for (const subCat of cat.subcategories) {
            if (subCat.slug === slug) {
              return subCat;
            }

            if (subCat.subcategories2?.length) {
              const foundInSubcategories2 = subCat.subcategories2.find(
                (subCat2) => subCat2.slug === slug
              );
              if (foundInSubcategories2) {
                return subCat;
              }
            }
          }
        }
      }

      return null; // No match found
    };

    if (isSmartAppliances) {
      if (customFilterData.length > 0 && !isSmartDishwashers) {
        return {
          category: customFilterData,
        };
      }

      if (customFilterData.length > 0 && isSmartDishwashers) {
        if (subCategoryIds || subCategory2Ids) {
          const currentFilterCategory = customFilterData.find(
            (cat) => cat.subCategoryId === Number(subCategoryIds)
          );

          if (currentFilterCategory)
            return {
              category: currentFilterCategory?.subcategories2,
            };

          const currentFiltersubCategory = customFilterData.find((cat) => {
            return cat.subcategories2.find(
              (subCat2) => subCat2.subCategory2Id === Number(subCategory2Ids)
            );
          });
          if (currentFiltersubCategory)
            return {
              category: currentFiltersubCategory?.subcategories2,
            };
        }

        return {
          category: customFilterData,
        };
      }
    }
    if (combineSlug === "products" || keyword)
      return { category: categoryList };
    const category = findCategoryBySlug(categoriesData, combineSlug);
    if (!category) return null;

    return {
      id: category.id,
      category: category.subcategories2 || category.subcategories || [],
    };
  }, [
    categoriesData,
    isCustomFilter,
    combineSlug,
    subCategoryIds,
    categoryIds,
    customFilterData,
    subCategory2Ids,
    categoryList,
  ]);

  const { category = [] } = subCategoriesData || {};
  const excludedSlugs = ["smart-appliances/browse-by-feature"];

  const generateHref = (category, mainSlug) => {
    const {
      isTagged,
      fieldIds,
      fieldValueIds,
      subCategoryId,
      subCategory2Id,
      criteriaId,
    } = category;

    if (isTagged) {
      // Filter out undefined or null values
      const validFieldIds = fieldIds?.filter((id) => id != null);
      const validFieldValueIds = fieldValueIds?.filter((id) => id != null);

      const query = [
        validFieldIds?.length ? `fieldIds=${validFieldIds.join(",")}` : null,
        validFieldValueIds?.length
          ? `fieldValueIds=${validFieldValueIds.join(",")}`
          : null,
        keyword ? `keyword=${keyword}` : null,
      ].filter(Boolean);

      if (
        isCustomFilter ||
        isBrandPage ||
        isLuxuryBrand ||
        isShop ||
        (isSmartAppliances && customFilterData.length > 0)
      ) {
        const updatedQuery = [
          subCategoryId ? `subCategoryIds=${subCategoryId}` : null,
          subCategory2Id ? `subCategory2Ids=${subCategory2Id}` : null,
          criteriaId ? `criteriaId=${criteriaId}` : null,
          ...query,
        ]
          .filter(Boolean)
          .join("&");
        const customSlug =
          isBrandPage || isLuxuryBrand || isShop ? pathname : mainSlug;

        return updatedQuery ? `${customSlug}?${updatedQuery}` : customSlug;
      }

      return query ? `${mainSlug}?${query.join("&")}` : mainSlug;
    }
    if (isBrandPage) {
      return `/all-brands/${categorySlug}${mainSlug}`;
    }
    if (isLuxuryBrand) {
      return mainSlug
        ? `/luxury-appliances-products/${mainSlug}`
        : `/luxury-appliances-products`;
    }
    if (
      isCustomFilter ||
      // isLuxuryBrand ||
      isShop ||
      (isSmartAppliances && customFilterData.length > 0)
    ) {
      const queryParams = [
        existingFieldIds ? `fieldIds=${existingFieldIds}` : null,
        existingFieldValuesIds
          ? `fieldValueIds=${existingFieldValuesIds}`
          : null,
        category?.id && `categoryIds=${category?.id}`,
        subCategoryId
          ? `${
              isSmartAppliances && !isSmartDishwashers
                ? `subCategory2Ids`
                : `subCategoryIds`
            }=${subCategoryId}`
          : null,
        subCategory2Id ? `subCategory2Ids=${subCategory2Id}` : null,
      ]
        .filter(Boolean)
        .join("&");

      const customSlug = isSmartAppliances
        ? mainSlug
        : isBrandPage || isLuxuryBrand || isShop
        ? pathname
        : slug;

      return queryParams ? `${customSlug}?${queryParams}` : customSlug;
    }

    return keyword ? `${mainSlug}?keyword=${keyword}` : mainSlug;
  };

  return (
    <div className="flex cursor-pointer gap-2 pb-2 lg:pb-0 lg:mb-[2px]">
      {category
        ?.filter((item) => !excludedSlugs.includes(item.slug))
        ?.map((item, index, catList) => {
          const slugPath = `/${[item?.slug].filter(Boolean).join("/")}`;
          const isActive =
            isSmartAppliances && customFilterData.length > 0
              ? Number(subCategory2Ids) === item.subCategory2Id ||
                Number(subCategoryIds) === item.subCategoryId ||
                Number(subCategory2Ids) === item.subCategoryId
              : isBrandPage || isLuxuryBrand
              ? pathname.includes(slugPath)
              : pathname === slugPath ||
                Number(subCategory2Ids) === item.subCategory2Id ||
                Number(subCategoryIds) === item.subCategoryId;

          const catImage = item?.categoryPicture;

          const title = capitalizeFirstLetter(
            item?.subCategoryName ||
              item?.subCategory2Name ||
              item?.categoryName ||
              item?.name
          );

          const href = generateHref(item, slugPath);

          const isSubCategory2 = item?.subCategory2Name ? true : false;
          return (
            <Link
              href={`${href}`}
              key={index}
              onClick={() =>
                !isActive &&
                addBreadcrum(isSubCategory2, item, catList, {
                  name: title,
                  url: href,
                })
              }
            >
              <div
                className={cn(
                  "max-w-[220px] min-w-[200px] w-full gap-5 items-center flex whitespace-break-spaces border-gray7 hover:border-red3 hover:text-red3 rounded-10 border-2 py-[15px] pl-[22px] pr-[15px] lg:py-[11px] lg:px-[15px] bg-white lg:gap-[15px] lg:max-w-[150px] lg:min-w-[150px] ",
                  isActive && "text-red3 border-red3 "
                )}
              >
                <DynamicImage
                  src={catImage || ProductDummyImg}
                  alt="product-img"
                  className="h-[50px] w-[50px]  lg:h-[40px] object-contain	lg:w-max"
                  quality="100"
                  objectFit="cover"
                  priority
                  width={50}
                  height={50}
                />

                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <h3 className="text-[12px] leading-[24px] lg:text-[10px] lg:leading-[17px] two-line-ellipsis">
                        {title}
                      </h3>
                    </TooltipTrigger>
                    <TooltipContent>
                      <h3 className="text-[12px] leading-[20px] lg:text-[10px] lg:leading-[14px] max-w-[120px]">
                        {title}
                      </h3>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </Link>
          );
        })}
    </div>
  );
};

export default TopSlider;
